// src/components/ProductCard.jsx
import React, { useState, useEffect } from 'react';
import api from '../api';

// Use base server URL from env (if set) or the current origin
const SERVER_BASE = process.env.REACT_APP_SERVER_URL || (typeof window !== 'undefined' ? window.location.origin : '');

function buildAvatarUrl(raw) {
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;         // full URL
  if (raw.startsWith('/')) return `${SERVER_BASE}${raw}`; // server path
  return `${SERVER_BASE}/uploads/${raw}`;            // filename only
}

function findVoterImageRaw(voter) {
  if (!voter) return null;
  const fields = [
    'profilePicture',
    'profilePicUrl',
    'profilePic',
    'profile_pic',
    'avatar',
    'photo',
    'image',
    'picture',
    'profilePicURL',
  ];
  for (const f of fields) {
    if (typeof voter[f] === 'string' && voter[f].trim() !== '') return voter[f].trim();
  }
  if (voter.profile && typeof voter.profile.picture === 'string' && voter.profile.picture.trim()) {
    return voter.profile.picture.trim();
  }
  return null;
}

export default function ProductCard({ product, me, onVoted, votingOpen = true }) {
  const [loading, setLoading] = useState(false);
  const [brokenSet, setBrokenSet] = useState(new Set());

  useEffect(() => {
    // reset broken set if product changes (so updates / new urls load)
    setBrokenSet(new Set());
  }, [product?._id, product?.voters?.length]);

  const votedProductId = me?.votedProduct?._id ?? me?.votedProduct;
  const alreadyVotedForThis = votedProductId && String(votedProductId) === String(product._id);
  const alreadyVotedForOther = Boolean(votedProductId && !alreadyVotedForThis);

  const voters = Array.isArray(product?.voters) ? product.voters : [];

  const onImgError = (key, e) => {
    e.currentTarget.onerror = null;
    setBrokenSet(prev => {
      const s = new Set(prev);
      s.add(key);
      return s;
    });
    e.currentTarget.src = 'https://plus.unsplash.com/premium_photo-1732757787588-29df717691f4?q=80&w=1054&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D';
  };

  const handleVote = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please login/signup to vote.');
      return;
    }

    if (!votingOpen) {
      alert('Voting is closed.');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/vote', { productId: product._id });
      // refresh parent
      if (onVoted) onVoted(res?.data?.product || undefined);
      window.dispatchEvent(new Event('userChanged'));
    } catch (err) {
      alert(err.response?.data?.message || 'Vote failed');
      if (onVoted) onVoted();
      window.dispatchEvent(new Event('userChanged'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      {product?.imageUrl && <img src={product.imageUrl} alt={product.name} className="card-img" />}
      <h3 style={{ color: '#ffb300' }}>{product?.name}</h3>
      <p>{product?.description}</p>

      

      {/* Voter avatars */}
      {voters.length > 0 && (
        <div className="voter-avatars" style={{ marginTop: 12 }}>
          <div style={{ marginBottom: 6, color: '#ccc', fontSize: 13 }}>Voted by</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {voters.slice(0, 8).map((voter, i) => {
              const key = voter._id || voter.id || i;
              const raw = findVoterImageRaw(voter);
              const url = raw ? buildAvatarUrl(raw) : null;
              const finalSrc = (!url || brokenSet.has(key)) ? 'https://via.placeholder.com/40?text=👤' : url;
              const label = voter.fullName || voter.email || 'User';

              return (
                <div
                  key={key}
                  className="avatar-wrap"
                  data-name={label}
                  title={label}
                  tabIndex={0}
                  aria-label={label}
                >
                  <img
                    src={finalSrc}
                    alt={label}
                    width={40}
                    height={40}
                    onError={(e) => onImgError(key, e)}
                    style={{
                      display: 'block',
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      border: '2px solid rgba(255,110,40,0.85)',
                      objectFit: 'cover',
                      boxSizing: 'border-box',
                      backgroundColor: '#222',
                    }}
                  />
                </div>
              );
            })}
            {voters.length > 8 && <div style={{ color: '#ddd', fontSize: 14 }}>+{voters.length - 8}</div>}
          </div>
        </div>
      )}



<div style={{ marginTop: 10 }}>
        {alreadyVotedForThis ? (
          <div className="voted-badge">✅ You voted for this</div>
        ) : alreadyVotedForOther ? (
          votingOpen ? (
            <button onClick={handleVote} disabled={loading} className="btn">
              {loading ? 'Changing…' : 'Change vote to this'}
            </button>
          ) : (
            <div className="info">You already voted for another product</div>
          )
        ) : (
          <button onClick={handleVote} disabled={loading || !votingOpen} className="btn">
            {loading ? 'Voting…' : (votingOpen ? 'Vote' : 'Voting closed')}
          </button>
        )}
      </div>

    </div>
  );
}
